import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, pick, unwrap, statusMeta, toDateInput, fromDateInput,
} from './routePlan/routePlanUtils';
import GeneralTab from './routePlan/GeneralTab';
import CustomersTab from './routePlan/CustomersTab';
import IntervalsTab from './routePlan/IntervalsTab';
import HistoryTab from './routePlan/HistoryTab';
import { useRoutePlanCustomers } from './routePlan/useRoutePlanCustomers';

const EMPTY_FORM = {
  textId: '', name: '', startDate: '', endDate: '', status: 'Draft',
  // #800 Distribütör kapsamı ÇOKLU: MultiChipSelect'in beklediği {value,label} dizisi.
  // BOŞ dizi geçerli bir seçimdir = plan tüm distribütörleri kapsar.
  vendors: [],
  createdBy: '', createdDate: null, updatedDate: null, customerCount: 0,
};

function rowToForm(r) {
  return {
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    startDate: toDateInput(pick(r, 'startDate', 'StartDate')),
    endDate: toDateInput(pick(r, 'endDate', 'EndDate')),
    status: pick(r, 'status', 'Status') ?? 'Draft',
    // #800 GetById artık kapsamı ad'larıyla birlikte döndürüyor (vendors: [{id,name,textId}]) →
    // çipler ek bir lookup isteği olmadan doğrudan çizilir. (1. turda yalnız VendorId dönüyordu ve
    // etiket çözülemediği için alan, kayıtta seçim olmasına rağmen boş görünüyordu.)
    vendors: (pick(r, 'vendors', 'Vendors') ?? []).map((v) => ({
      value: pick(v, 'id', 'Id'),
      label: [pick(v, 'textId', 'TextId'), pick(v, 'name', 'Name')].filter(Boolean).join(' - '),
    })),
    createdBy: pick(r, 'createdBy', 'CreatedBy') ?? '',
    createdDate: pick(r, 'createdDate', 'CreatedDate') ?? null,
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate') ?? null,
    customerCount: pick(r, 'customerCount', 'CustomerCount') ?? 0,
  };
}

export function RoutePlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  usePageMeta(isNew ? 'Yeni Rut Planı' : 'Rut Planı Detay', 'Döngüsel ziyaret planı tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [isEdit, setIsEdit] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);

  // Menü aksiyon hakları: yeni kayıt Add, mevcut kayıt Update ister.
  // canSave sekmelere de taşınır (isEditable) — Müşteri Ata/Kaydır/Ziyaret Sırası gibi
  // kaydı değiştiren alt-aksiyonlar zaten isEdit'e bağlı, böylece hepsi kapanır.
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  // Stabil (useCallback) — hook'un reload/flush bağımlılıkları her render'da değişmesin.
  const showToast = useCallback((type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); }, []);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  /**
   * #821 Kapsam distribütörlerinin id listesi — REFERANSI STABİL olmalı.
   *
   * Eskiden JSX içinde `(form.vendors || []).map(...)` ile üretiliyordu, yani HER RENDER'DA
   * yeni bir dizi. AddCustomersModal bu diziyi arama fonksiyonunun bağımlılığında taşıdığı
   * için, herhangi bir sebeple bu sayfa yeniden render olduğunda (örneğin bir toast
   * gösterildiğinde) arama yeniden çalışıp müşteri seçimini sıfırlıyordu: haritadan alan
   * seçen kullanıcı "N müşteri seçildi" toast'ını görüyor ama toast'ın kendisi seçimi
   * siliyordu ve "Seçilenleri Ekle" pasif kalıyordu.
   */
  const vendorIds = useMemo(
    () => (form.vendors || []).map((v) => v.value),
    [form.vendors],
  );

  // #189 Müşteriler sekmesi TASLAK durumu — parent'ta tutulur ki sekme değişince kaybolmasın.
  // Bekleyen değişiklikler yalnız "Değişiklikleri Kaydet"te yazılır, "Vazgeç"te atılır.
  const customers = useRoutePlanCustomers(isNew ? null : id, showToast);
  const custLoadedRef = useRef(false);
  const custPending = customers.pending.total;

  // Müşteri havuzunu yalnız bir kez (sekme ilk açıldığında) yükle; tekrar açılışta hook
  // durumu (bekleyen değişiklikler dahil) korunur — her mount'ta reload bekleyenleri silerdi.
  useEffect(() => {
    if (!isNew && activeTab === 'customers' && !custLoadedRef.current) {
      custLoadedRef.current = true;
      customers.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, activeTab]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rut planı alınamadı (${res.status})`);
      setForm(rowToForm(unwrap(await res.json())));
    } catch (err) {
      showToast('error', err.message || 'Rut planı alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, showToast]);

  useEffect(() => { load(); }, [load]);

  const buildBody = () => ({
    textId: form.textId?.trim() || null,
    name: form.name.trim(),
    startDate: fromDateInput(form.startDate),
    endDate: fromDateInput(form.endDate),
    // #800 Çoklu kapsam; boş dizi gönderilir ve backend bunu "tüm distribütörler" sayar.
    vendorIds: (form.vendors || []).map((v) => v.value),
    status: form.status,
  });

  const validate = () => {
    if (!form.name.trim()) return 'Rut adı zorunlu.';
    // #800 Distribütör ARTIK ZORUNLU DEĞİL: boş bırakmak "tüm distribütörler" demektir.
    if (!form.startDate) return 'Başlangıç tarihi zorunlu.';
    if (!form.endDate) return 'Bitiş tarihi zorunlu.';
    if (form.startDate >= form.endDate) return 'Bitiş tarihi başlangıç tarihinden büyük olmalı!';
    return null;
  };

  /**
   * #805 KAPSAM ↔ MÜŞTERİ DİSTRİBÜTÖRÜ KAPISI.
   *
   * Havuz eklerken kapsamla daraltılıyordu, ama kapsamın KENDİSİ sonradan değiştirilebiliyordu:
   * plan Trabzon distribütörüyle kurulup 64 Trabzon müşterisi yükleniyor, sonra Düzenle'de kapsam
   * Bursa yapılıp kaydediliyordu. Ekran itiraz etmiyordu; geriye kapsamıyla hiç örtüşmeyen bir
   * plan kalıyordu (müşteri havuzu kapsam dışı, temsilci adayları başka distribütörden).
   *
   * 1. turda yalnız SORULUYORDU ("yine de kaydet?") ve kullanıcı geçince kayıt yapılıyordu.
   * Saha (#805 NOK, DEMO/MİX rutu): "Uyarı geldi FAKAT kayıt işlemini de yaptı. Kaydı hiç
   * yapmamalı." Artık kayıt REDDEDİLİR; kullanıcıya iki çıkış yolu söylenir (kapsamı düzelt
   * VEYA kapsam dışı müşterileri havuzdan çıkar). Aynı kapı sunucuda da vardır
   * (RoutePlansController.Update) — ekrandaki engel API'yi doğrudan çağıranı durdurmaz.
   *
   * Sayım BEKLEYEN değişiklikleri hesaba katar: "çıkar" ile işaretlenmiş satırlar (_removed)
   * sayılmaz, böylece kapsam dışı müşterileri çıkarıp aynı kaydetmede kapsamı değiştirmek
   * geçerli bir akıştır.
   *
   * Kapsam boşsa ("tüm distribütörler") kontrol yapılmaz: kapsam dışı diye bir şey yoktur.
   * Distribütörü tanımsız müşteriler de sayılmaz — onlar başka bir distribütöre ait değildir.
   *
   * @returns {Promise<boolean>} kaydetmeye devam edilecek mi
   */
  const assertVendorConsistency = async () => {
    const scope = new Set((form.vendors || []).map((v) => String(v.value)));
    if (scope.size === 0) return true;

    // Müşteriler sekmesine hiç girilmemişse havuz elde değildir; kontrol için bir kez okunur.
    // (Kullanıcı Genel sekmesinden doğrudan kaydedebiliyor — asıl kaçak senaryo da budur.)
    let rows = customers.loaded ? customers.rows.filter((r) => !r._removed) : null;
    if (!rows) {
      try {
        const res = await fetch(`${API_BASE}/${id}/customers`, { headers: authHeaders() });
        // Havuz okunamadıysa istemci karar veremez; kaydı burada bloklamayız — sunucudaki
        // aynı kapı (RoutePlansController.Update) yine de kapsam dışı kaydı reddeder.
        if (!res.ok) return true;
        rows = (unwrap(await res.json()) || []).map((r) => ({
          vendorId: pick(r, 'vendorId', 'VendorId'),
          vendorName: pick(r, 'vendorName', 'VendorName'),
        }));
      } catch {
        return true;
      }
    }

    const outside = new Map(); // distribütör adı → müşteri sayısı
    rows.forEach((r) => {
      if (!r.vendorId || scope.has(String(r.vendorId))) return;
      const label = r.vendorName || 'Tanımsız distribütör';
      outside.set(label, (outside.get(label) || 0) + 1);
    });
    if (outside.size === 0) return true;

    const detail = [...outside.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => `• ${name} (${count} müşteri)`)
      .join('\n');
    // Uzun ve maddeli metin toast'a sığmıyor; kullanıcı ne yapacağını okumadan geçmemeli.
    window.alert(
      `KAYIT YAPILMADI.\n\nMüşteri havuzunda, rut tanımının distribütör kapsamı DIŞINDA kalan `
      + `müşteriler var:\n\n${detail}\n\n`
      + 'Kaydedebilmek için ikisinden birini yapın:\n'
      + '  1) Genel sekmesindeki distribütör seçimini bu müşterileri kapsayacak şekilde düzeltin,\n'
      + '  2) veya Müşteriler sekmesinde kapsam dışı müşterileri havuzdan çıkarın '
      + '(uyarı şeridindeki “Müşteri Sil” ya da satırdaki çıkar işlemi).',
    );
    showToast('error', 'Kapsam dışı müşteri var — kayıt yapılmadı.');
    return false;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { showToast('error', err); return; }
    // #805 Yeni kayıtta havuz henüz yoktur; kontrol yalnız mevcut planda anlamlıdır.
    if (!isNew && !(await assertVendorConsistency())) return;
    setSaving(true);
    try {
      // #189 Müşteriler sekmesindeki BEKLEYEN değişiklikleri (ekleme/çıkarma/program) tek
      // seferde yaz. #805: bu artık BAŞLIK KAYDINDAN ÖNCE olur — kullanıcı kapsam dışı
      // müşterileri "çıkar" ile işaretleyip kapsamı aynı kaydetmede değiştirdiğinde,
      // sunucudaki kapsam kapısı onları havuzda görmemeli.
      if (!isNew && custPending > 0) {
        await customers.flush();
      }
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        const newId = pick(unwrap(payload), 'id', 'Id');
        showToast('success', 'Rut planı oluşturuldu.');
        if (newId) { navigate(`/route-plans/${newId}`, { replace: true }); return; }
      }
      showToast('success', 'Değişiklikler kaydedildi.');
      setIsEdit(false);
      load();
    } catch (e) {
      showToast('error', e.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  // "Vazgeç": Genel formu sunucu kopyasından geri yükle + Müşteriler sekmesindeki bekleyen
  // değişiklikleri at (#189). Düzenleme modundan çık.
  const handleCancel = () => {
    setIsEdit(false);
    customers.discard();
    load();
  };

  // Üst başlık şeridindeki "← Geri": kaydedilmemiş müşteri değişikliği varsa onay iste.
  const handleBack = () => {
    if (custPending > 0 && !window.confirm(`Müşteriler sekmesinde kaydedilmemiş ${custPending} değişiklik var. Yine de çıkmak istiyor musunuz?`)) return;
    navigate('/route-plans');
  };

  const m = statusMeta(form.status);
  // Yetki yoksa düzenleme modu açılmaz (yeni kayıtta isEdit başlangıçta true'dur).
  const isEditable = isEdit && canSave;
  // Müşteri sayısı: havuz yüklendiyse canlı (taslak dahil) aktif sayı, değilse Genel DTO sayısı.
  const custCount = customers.loaded ? customers.activeCount : form.customerCount;
  const TABS = [
    { key: 'general', label: 'Genel', icon: '📄' },
    { key: 'customers', label: `Müşteriler${custCount ? ` ${custCount}` : ''}${custPending > 0 ? ' •' : ''}`, icon: '👥' },
    { key: 'intervals', label: 'Ziyaret Programı', icon: '🗓' },
    { key: 'history', label: 'Geçmiş', icon: '🕘' },
  ];

  return (
    <div style={{ ...s.container, padding: '4px 2px' }}>
      {/* Başlık şeridi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <button style={s.btnSecondary} onClick={handleBack}>← Geri</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={titleIcon} aria-hidden="true">🗺️</span>
          <span style={{ fontSize: 19, fontWeight: 800 }}>{form.name || (isNew ? 'Yeni Rut Planı' : '...')}</span>
          {!isNew && <StatusBadge tone={m.tone}>{m.label}</StatusBadge>}
          {/* #189 Kaydedilmemiş müşteri değişikliği rozeti — kullanıcı taslak durumunu görsün. */}
          {custPending > 0 && (
            <span style={pendingBadge} title="Müşteriler sekmesinde kaydedilmemiş değişiklikler var">
              ● {custPending} kaydedilmemiş değişiklik
            </span>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          {isEditable ? (
            <>
              {!isNew && <button style={s.btnSecondary} onClick={handleCancel} disabled={saving}>Vazgeç</button>}
              <button style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Kaydediliyor...' : (isNew ? 'Oluştur' : 'Değişiklikleri Kaydet')}
              </button>
            </>
          ) : (
            canEdit && !isNew && <button style={s.btnPrimary} onClick={() => setIsEdit(true)}>✎ Düzenle</button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary, #6b7280)' }}>Yükleniyor…</div>
      ) : (
        <>
          {/* Sekmeler */}
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.1))', marginBottom: 20 }}>
            {TABS.map((t) => {
              const disabled = isNew && t.key !== 'general';
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  disabled={disabled}
                  onClick={() => setActiveTab(t.key)}
                  title={disabled ? 'Önce rut planını kaydedin' : undefined}
                  style={{
                    border: 'none', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
                    padding: '11px 18px', fontSize: 14, fontWeight: active ? 800 : 500,
                    color: disabled ? 'var(--text-disabled, #c0c4cc)' : active ? 'var(--accent)' : 'var(--text-primary)',
                    borderBottom: active ? '2.5px solid var(--accent)' : '2.5px solid transparent',
                  }}
                >
                  <span aria-hidden="true" style={{ marginRight: 7 }}>{t.icon}</span>{t.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'general' && (
            <GeneralTab form={form} set={set} isEdit={isEditable} isNew={isNew} planId={id} showToast={showToast} />
          )}
          {/* #800 Müşteri havuzu kapsamdaki distribütörlerle daraltılır; kapsam boşsa
              (tüm distribütörler) daraltma yapılmaz → vendorIds boş dizi gider. */}
          {activeTab === 'customers' && !isNew && (
            <CustomersTab planId={id} isEdit={isEditable} showToast={showToast} customers={customers}
              vendorIds={vendorIds} />
          )}
          {activeTab === 'intervals' && !isNew && (
            <IntervalsTab planId={id} isEdit={isEditable} showToast={showToast} />
          )}
          {/* Geçmiş: program satırlarının versiyon (ValidFrom/ValidTo) günlüğü — salt-okunur. */}
          {activeTab === 'history' && !isNew && (
            <HistoryTab planId={id} showToast={showToast} />
          )}
        </>
      )}

      <Toast toast={toast} />
    </div>
  );
}

const titleIcon = {
  width: 34, height: 34, borderRadius: 10, background: 'rgba(0,115,234,0.12)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
};

// #189 Kaydedilmemiş müşteri değişikliği rozeti (amber).
const pendingBadge = {
  fontSize: 12, fontWeight: 700, color: '#92600a', background: 'rgba(253,171,61,0.14)',
  border: '1px solid rgba(253,171,61,0.4)', padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
};

export default RoutePlanDetailPage;
